/**
 * Configuration for model-specific benchmark notes
 */

export interface ModelNote {
  id: string; // Model ID
  title: string; // Note title
  content: string; // Note content in markdown or HTML
}

// Notes for specific models
export const MODEL_NOTES: ModelNote[] = [
  {
    id: 'ept2',
    title: 'About EPT-2 Benchmarks',
    content: `
      <p>
        EPT-2 is our latest Earth Physics Transformer model. The benchmarks shown here compare the model's 
        performance against various baseline models from Jan 1, 2023 to Dec 31, 2023.
      </p>

      <p class="mt-2">
        <strong>Geographies:</strong>
      </p>
      <ul class="list-disc pl-5 space-y-1 mt-1">
        <li>Global (not EPT1.5)</li>
        <li>Europe</li>
        <li>US</li>
      </ul>

      <p class="mt-2">
        <strong>Parameters:</strong>
      </p>
      <ul class="list-disc pl-5 space-y-1 mt-1">
        <li>Air temperature 2m</li>
        <li>Air pressure at mean sea level</li>
        <li>Wind speed 10m</li>
        <li>Wind direction 10m</li>
        <li>Wind speed 100m</li>
        <li>Wind direction 100m</li>
        <li>Geopotential 500 hPa</li>
      </ul>

      <p class="mt-2">
        <strong>Forecast runs:</strong>
      </p>
      <ul class="list-disc pl-5 space-y-1 mt-1">
        <li>EPT2: 0-240h: every 1h; 240-480h: every 6 hours</li>
        <li>Aurora, AIFS: 0-480h: every 6 hours</li>
        <li>EC IFS, EC ENS Mean: 0-240h: every 6 hours</li>
      </ul>

      <p class="mt-2">
        <strong>Time coverage: 2023</strong>
      </p>
      <ul class="list-disc pl-5 space-y-1 mt-1">
        <li>EPT2, Aurora, AIFS: every fourth day, 00, 06, 12, and 18 ICs</li>
        <li>EC IFS: every fourth day, 00 and 12 ICs</li>
        <li>EC ENS Mean: every tenth day (1st/11th/21st/31st of the month), 00 and 12 ICs</li>
      </ul>

      <p class="mt-2">
        <strong>Note on comparison with EPT-1.5:</strong> EPT-1.5 was benchmarked using only the 00 initial condition (IC), while EPT-2 includes all ICs (00, 06, 12, and 18). Due to this difference, scores cannot be compared 1:1 between EPT-1.5 and EPT-2. For accurate performance evaluation, please compare each model against baseline models like EC IFS.
      </p>

      <p class="mt-2">
        <strong>Temporal resolution disclaimer:</strong> ECMWF only provides 6-hourly historical forecasts, while Jua models provide hourly hindcasts. This is why you can see Jua model scores at hourly intervals, but ECMWF scores only every 6 hours on the charts. Similarly, the Aurora model provides data only at 6-hourly intervals.
      </p>
    `,
  },
  {
    id: 'ept1_5',
    title: 'About EPT-1.5 Benchmarks',
    content: `
      <p>
        EPT-1.5 is our production Earth Physics Transformer model. These benchmarks show the model's 
        performance against baseline models for the full year of 2023.
      </p>
      <p>
        <strong>Key improvements over EPT-1.0:</strong>
      </p>
      <ul class="list-disc pl-5 space-y-1 mt-2">
        <li>Better accuracy for wind and temperature forecasts</li>
        <li>Improved handling of diurnal cycles</li>
        <li>Enhanced performance in mountainous regions</li>
      </ul>
      <p class="mt-2">
        <strong>Initial conditions:</strong> EPT-1.5 was benchmarked using only the 00 initial condition, as this was most requested by customers. Our latest EPT-2 benchmarks have been expanded to include all initial conditions per day (00, 06, 12, and 18).
      </p>

      <p class="mt-2">
        <strong>Temporal resolution disclaimer:</strong> ECMWF only provides 6-hourly historical forecasts, while Jua models provide hourly hindcasts. This is why you can see Jua model scores at hourly intervals, but ECMWF scores only every 6 hours on the charts.
      </p>
    `,
  },
  {
    id: 'ept1_5_early',
    title: 'About EPT-1.5 Early Benchmarks',
    content: `
      <p>
        EPT-1.5 Early represents the early release version of EPT-1.5. These benchmarks cover a shorter period 
        from March 18, 2023 to July 31, 2023.
      </p>
      <p>
        <strong>Note:</strong> This early version had slightly different training parameters than the final EPT-1.5 model.
      </p>

      <p class="mt-2">
        <strong>Time period disclaimer:</strong> EPT-1.5 Early benchmarks are based on a subset of 2023 (March 18 to July 31) rather than the full year. This limited time period means benchmark values may differ from the full EPT-1.5 benchmarks, as seasonal variations and specific weather patterns during this period can influence overall performance metrics.
      </p>

      <p class="mt-2">
        <strong>Temporal resolution disclaimer:</strong> ECMWF only provides 6-hourly historical forecasts, while Jua models provide hourly hindcasts. This is why you can see Jua model scores at hourly intervals, but ECMWF scores only every 6 hours on the charts.
      </p>
    `,
  },
];

/**
 * Function to get notes for a specific model
 */
export function getModelNotes(modelId: string): ModelNote | undefined {
  return MODEL_NOTES.find((note) => note.id === modelId);
}
